import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Bot, Plus, Trash2, Loader2, Pencil, FileText, ListChecks,
} from "lucide-react";

const AGENT_TYPE_LABELS = {
  receptionist: "Receptionist",
  sales: "Sales",
  support: "Support",
  booking: "Booking",
  custom: "Custom",
};
const AGENT_TYPE_ACCENT = {
  receptionist: "bg-sky-100 text-sky-800 border-sky-200",
  sales: "bg-emerald-100 text-emerald-800 border-emerald-200",
  support: "bg-amber-100 text-amber-800 border-amber-200",
  booking: "bg-purple-100 text-purple-800 border-purple-200",
  custom: "bg-zinc-100 text-zinc-800 border-zinc-200",
};

/**
 * AI Phone Agents — list page. Clicking a card opens the full
 * stand-alone editor at `/ai-phone-agents/:agent_id`. The "New
 * agent" flow uses a tiny modal to capture name + type only, then
 * creates the stub via the API and navigates straight into the
 * editor.
 */
export default function AiPhoneAgentsPage() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai-phone-agents");
      setAgents(data.agents || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load agents");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const handleDelete = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm("Delete this agent and all its reference docs?")) return;
    try {
      await api.delete(`/ai-phone-agents/${id}`);
      toast.success("Agent deleted");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="p-6 max-w-7xl" data-testid="ai-phone-agents-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-purple-100 text-purple-700">
          <Bot className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="label-overline">AI · PHONE AGENTS</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">AI Phone Agents</h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Configure the receptionist, sales, support, and booking bots that answer your inbound calls. Each
            agent has its own script, persona, question playbook, and a library of reference docs it can quote.
          </p>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="ai-agent-new-btn"
          className="rounded-sm bg-purple-700 hover:bg-purple-800">
          <Plus className="h-4 w-4 mr-1.5" /> New agent
        </Button>
      </div>

      {loading ? (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-12 text-center">
            <Loader2 className="h-6 w-6 animate-spin inline" />
          </CardContent>
        </Card>
      ) : agents.length === 0 ? (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-12 text-center">
            <Bot className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
            <div className="font-display font-bold text-lg mb-1">No agents yet</div>
            <p className="text-sm text-zinc-500 mb-4">Create your first AI receptionist to start handling inbound calls automatically.</p>
            <Button onClick={() => setCreating(true)} data-testid="ai-agent-empty-create"
              className="rounded-sm bg-purple-700 hover:bg-purple-800">
              <Plus className="h-4 w-4 mr-1.5" /> Create first agent
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
          data-testid="ai-agent-list">
          {agents.map((a) => (
            <Link key={a.agent_id} to={`/ai-phone-agents/${a.agent_id}`}
              data-testid={`ai-agent-card-${a.agent_id}`}>
              <Card className="rounded-sm border-zinc-200 hover:border-purple-300 hover:shadow-sm transition cursor-pointer group">
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className={`rounded-sm text-[10px] ${AGENT_TYPE_ACCENT[a.agent_type] || AGENT_TYPE_ACCENT.custom}`}>
                      {AGENT_TYPE_LABELS[a.agent_type] || a.agent_type}
                    </Badge>
                    {!a.is_active && (
                      <Badge variant="outline" className="rounded-sm text-[10px] bg-zinc-100 text-zinc-600">Inactive</Badge>
                    )}
                    <div className="ml-auto flex gap-1 opacity-0 group-hover:opacity-100 transition">
                      <Pencil className="h-3.5 w-3.5 text-zinc-400" />
                      <button onClick={(e) => handleDelete(a.agent_id, e)}
                        className="text-zinc-400 hover:text-red-600" title="Delete">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <h3 className="font-display font-bold text-base mt-2 mb-1 truncate">{a.name}</h3>
                  {a.description && (
                    <p className="text-xs text-zinc-500 line-clamp-2 mb-2">{a.description}</p>
                  )}
                  <div className="flex items-center gap-3 text-[10px] text-zinc-500 font-mono mt-3">
                    <span><FileText className="h-3 w-3 inline mr-0.5" />{a.reference_count || 0} refs</span>
                    <span><ListChecks className="h-3 w-3 inline mr-0.5" />{(a.questions || []).length} questions</span>
                    <span className="ml-auto">{(a.assigned_phone_numbers || []).length} numbers</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {creating && (
        <NewAgentDialog
          onCreated={(id) => navigate(`/ai-phone-agents/${id}`)}
          onClose={() => setCreating(false)} />
      )}
    </div>
  );
}

/* ───────────────────────── New-agent stub modal ───────────────────────── */
function NewAgentDialog({ onCreated, onClose }) {
  const [form, setForm] = useState({ name: "", agent_type: "receptionist" });
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required.");
    setSaving(true);
    try {
      const { data } = await api.post("/ai-phone-agents", {
        name: form.name.trim(),
        agent_type: form.agent_type,
        is_active: true,
      });
      toast.success(`Created "${data.name}" — opening editor…`);
      onClose();
      onCreated(data.agent_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-sm" data-testid="ai-agent-new-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-xl">New phone agent</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-500 -mt-2">
          Give the agent a name and type. You&apos;ll fill in the script, persona, questions, and references on the next screen.
        </p>
        <div className="space-y-3 mt-2">
          <div>
            <div className="label-overline text-[10px] mb-1">Name</div>
            <Input value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") create(); }}
              placeholder="e.g. IT Sales agent"
              autoFocus
              data-testid="ai-agent-new-name"
              className="rounded-sm" />
          </div>
          <div>
            <div className="label-overline text-[10px] mb-1">Type</div>
            <Select value={form.agent_type}
              onValueChange={(v) => setForm({ ...form, agent_type: v })}>
              <SelectTrigger className="rounded-sm" data-testid="ai-agent-new-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(AGENT_TYPE_LABELS).map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-zinc-200 mt-3">
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={create} disabled={saving}
            className="rounded-sm bg-purple-700 hover:bg-purple-800"
            data-testid="ai-agent-new-create">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Create &amp; open editor
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
